import { inject, Injectable } from '@angular/core'
import type { Observable } from 'rxjs'

import { HttpService } from '@app/services/http.service'

import type {
  BoostMessageResponse,
  GetConversationResponse,
  SendMessagesPayload,
  SendMessagesResponse,
  UnlockConversationResponse,
  UnlockMessagePhotoResponse,
  UnlockReadReceiptsResponse,
  UnlockVoiceMessageResponse,
} from '../models/api.model'
import type { MessageSource } from '../models/message-source.model'
import type { MessageBoostType } from '../modules/boost/models/message-boost-type'
import type { ConversationReadReceiptsUnlockType } from '../modules/read-receipts/models/conversation-read-receipts-unlock-type.model'

@Injectable({ providedIn: 'root' })
export class ConversationService {
  private readonly httpService = inject(HttpService)

  getConversation(
    profile_browse_id: string,
    cursor?: string,
    isSuggestedProfile?: boolean,
  ): Observable<GetConversationResponse> {
    return this.httpService.get<GetConversationResponse>(`conversations/${profile_browse_id}`, {
      params: {
        ...(cursor !== undefined ? { cursor } : {}),
        ...(isSuggestedProfile ? { suggestion: true } : {}),
      },
    })
  }

  sendMessages(
    profile_browse_id: string,
    payload: SendMessagesPayload,
    source: MessageSource,
  ): Observable<SendMessagesResponse> {
    return this.httpService.post<SendMessagesResponse>(`/conversations/messages`, {
      profile_browse_id,
      ...payload,
      source,
    })
  }

  removeConversation(profile_browse_id: string): Observable<unknown> {
    return this.httpService.delete(`conversations/${profile_browse_id}`)
  }

  unlockConversation(profile_browse_id: string, source: MessageSource): Observable<UnlockConversationResponse> {
    return this.httpService.put<UnlockConversationResponse>(`conversations/${profile_browse_id}/unlock`, {
      source,
    })
  }

  unlockReadReceipts(
    profile_browse_id: string,
    unlock_read_receipts_type: ConversationReadReceiptsUnlockType,
    source: MessageSource,
  ): Observable<UnlockReadReceiptsResponse> {
    return this.httpService.put<UnlockReadReceiptsResponse>(`conversations/${profile_browse_id}/unlock_read_receipts`, {
      unlock_read_receipts_type,
      source,
    })
  }

  unlockMessagePhoto(message_timestamp: string, source: MessageSource): Observable<UnlockMessagePhotoResponse> {
    return this.httpService.put<UnlockMessagePhotoResponse>(`/conversations/messages/unlock`, {
      message_timestamp,
      source,
    })
  }

  boostMessage(
    message_timestamp: string,
    boost_type: MessageBoostType,
    source: MessageSource,
  ): Observable<BoostMessageResponse> {
    return this.httpService.put<BoostMessageResponse>(`/conversations/messages/boost`, {
      message_timestamp,
      boost_type,
      source,
    })
  }

  reportMessagePhoto(message_timestamp: string, reason: string, source: MessageSource): Observable<unknown> {
    return this.httpService.put(`/conversations/messages/report`, {
      message_timestamp,
      reason,
      source,
    })
  }

  open(message_timestamp: string, source: MessageSource): Observable<BoostMessageResponse> {
    return this.httpService.put<BoostMessageResponse>(`/conversations/messages/open`, {
      message_timestamp,
      source,
    })
  }

  unlockVoiceMessage(message_timestamp: string, source: MessageSource): Observable<UnlockVoiceMessageResponse> {
    return this.httpService.put<UnlockVoiceMessageResponse>(`/conversations/messages/unlock`, {
      message_timestamp,
      source,
    })
  }

  play(message_timestamp: string, source: MessageSource, profile_browse_id: string): Observable<void> {
    return this.httpService.put<void>(`/conversations/messages/play`, {
      message_timestamp,
      source,
      profile_browse_id,
    })
  }
}
